import type { ContentStatus } from '@shared/constants/statuses';

/**
 * CMS-managed i18n key store. Each row is a single localized value for a
 * (namespace, key, languageCode) tuple consumed by external FE apps via the
 * public endpoint `GET /api/v1/public/i18n/{lng}/{ns}`.
 *
 * `format` is a stored presentation hint ("Text" | "Markdown") — the CMS only
 * stores it; it does NOT render a markdown editor. `status` follows the same
 * content convention as glossary (BE int: 7 = Published, else Draft) and feeds
 * `StatusBadge kind="content"` (DRAFT | PUBLISHED only here).
 */
export const TRANSLATION_FORMAT = {
  TEXT: 'Text',
  MARKDOWN: 'Markdown',
} as const;

export type TranslationFormat = (typeof TRANSLATION_FORMAT)[keyof typeof TRANSLATION_FORMAT];

/** FE-facing status — only Draft / Published are meaningful for translations. */
export type TranslationStatus = Extract<ContentStatus, 'DRAFT' | 'PUBLISHED'>;

export interface TranslationKey {
  id: string;
  namespace: string;
  key: string;
  languageCode: string;
  value: string;
  description?: string;
  format: TranslationFormat;
  status: TranslationStatus;
  createdAt: string;
  updatedAt: string;
}

export interface TranslationListQuery {
  page?: number;
  pageSize?: number;
  q?: string;
  namespace?: string;
  /** Filter to a single language row (BE `languageCode` param, e.g. "tr" | "en"). */
  languageCode?: string;
  status?: TranslationStatus;
  sort?: 'key' | 'namespace' | 'recent';
}

/**
 * A distinct namespace with its live-key count and the language codes present in
 * it — served by `GET /api/v1/localization/namespaces`. Drives the namespace
 * directory (filter sidebar) and the namespace manager (rename / delete).
 */
export interface NamespaceSummary {
  namespace: string;
  keyCount: number;
  languageCodes: string[];
}

/** Result of a namespace rename / delete (`affectedRows` = moved / soft-deleted rows). */
export interface NamespaceMutationResult {
  namespace: string;
  newNamespace?: string;
  affectedRows: number;
}

export interface TranslationListResponse {
  items: TranslationKey[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface CreateTranslationInput {
  namespace: string;
  key: string;
  languageCode: string;
  value: string;
  description?: string;
  format: TranslationFormat;
  status: TranslationStatus;
}

export interface UpdateTranslationInput {
  namespace?: string;
  key?: string;
  languageCode?: string;
  value?: string;
  description?: string;
  format?: TranslationFormat;
  status?: TranslationStatus;
}
